import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import * as actions from '@actions';
import * as actionTypes from '@actionTypes';
import Link from 'next/link';
import {
  DownOutlined,
  UserOutlined,
  FileTextOutlined,
  LogoutOutlined,
  MenuOutlined,
  ReloadOutlined,
  PhoneOutlined
} from 'antd/icons';
import { Dropdown, Avatar, Button, MenuProps } from 'antd';
import useAvatar from 'vl-common/src/hooks/useAvatar';
import { useQueryClient } from '@tanstack/react-query';
import useTheme from '@hooks/useTheme';
import Head from 'next/head';
import { useIsStandalone } from '@hooks/useSafariPrompts';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import PleaseLogIn from '@components/Modal/PleaseLogIn';
import { usePleaseLogIn, usePleaseLogInAndRedirect } from '@hooks/usePleaseLogIn';
import useIsRestricted from '@hooks/useIsRestricted';
import useAuth from '@vl-core/useAuth';
import usePolicies from '@hooks/usePolicies';
import { ConfirmationModal } from 'vl-common/src/components/Modals';
import { useExtremeZoom } from '@hooks/useZoom';
import { UnstyledButton } from 'vl-common/src/components/UnstyledButton';
import { requestSuccess } from '../../../store/actions/action';
import { NavigationHeader } from './NavigationHeader';
import { clearPreviouslyPromisedRooms } from '@hooks/useTestPatientRoom';
import { Lobby } from '@lib/Lobby';
import { AutomatedClinicianLauncher } from '../../AutomatedClinicianLauncher';
import { useRuntimeConfig } from 'vl-core/useRuntimeConfig';
import { AXA_LOGO, NEW_HBS_LOGO } from '@lib/context/themes';
import useIsClinicianSubdomain from '@hooks/useIsClinicianSubdomain';
import { resetDocmanGPRejectedModal } from '@components/Patients/Docman/DocmanGPRejectedModal';

type MenuDefinition = {
  name: string;
  link: string;
  restricted?: boolean;
  testid: string;
};

type PageType = 'patients' | 'clinician' | 'login';

const menuList: Record<PageType, MenuDefinition[]> = {
  clinician: [
    {
      name: 'Dashboard',
      link: '/clinician',
      testid: 'clinician-dashboard-link'
    },
    {
      name: 'Appointments',
      link: '/clinician/appointments',
      testid: 'clinician-appointments-link'
    },
    {
      name: 'Cases',
      link: '/clinician/cases',
      testid: 'clinician-cases-link'
    },
    {
      name: 'Patients',
      link: '/clinician/patients',
      testid: 'clinician-patients-link'
    },
    {
      name: 'Reports',
      link: '/clinician/reports',
      testid: 'clinician-reports-link'
    },
    {
      name: 'Support',
      link: '/clinician/support',
      testid: 'clinician-support-link'
    }
  ],
  patients: [
    {
      name: 'Dashboard',
      link: '/patients',
      testid: 'patients-dashboard-link'
    },
    {
      name: 'Appointments',
      link: '/patients/appointments',
      restricted: true,
      testid: 'patients-appointments-link'
    },
    {
      name: 'Support',
      link: '/patients/support',
      testid: 'patients-support-link'
    }
  ],
  login: []
};

const mobileNavMenuDropdownMenu = (
  menu: PageType,
  isRestricted: boolean,
  setModalOpen: (s: boolean) => void
): MenuProps['items'] =>
  menuList[menu].map((item, index) => ({
    key: index,
    label:
      item.restricted && isRestricted ? (
        <UnstyledButton
          key={item.link}
          className="mobile-nav-link"
          onClick={() => setModalOpen(true)}
          data-testid={item.testid}
        >
          {item.name}
        </UnstyledButton>
      ) : (
        <Link href={item.link} className="mobile-nav-link" data-testid={item.testid}>
          {item.name}
        </Link>
      )
  }));

const navMenuStyles = css`
  display: flex;
  flex-basis: 100%;
  justify-content: flex-end;

  /* Centre the nav content above 1024px */
  @media (min-width: 1024px) {
    justify-content: center;
  }

  .desktop {
    /* Hide the desktop nav at small sizes */
    display: none;

    /* Show the desktop nav above 1024px */
    @media (min-width: 1024px) {
      display: block;
    }
  }
  .mobile {
    /* Show the mobile nav at small sizes */
    display: block;

    /* Hide the mobile nav above 1024px */
    @media (min-width: 1024px) {
      display: none;
    }
  }
  .hamburger {
    color: var(--black);
    font-size: 1.25rem;
    margin: 0 0.75rem;
  }

  .nav-link.active,
  .mobile-nav-link.active {
    color: var(--productPrimary);
  }
  .nav-link {
    color: var(--black);
    margin-right: 1em;
    &:last-child {
      margin-right: 0;
    }
  }
`;

interface NavMenuProps {
  menu: PageType;
  onShiftTab: () => void;
}

const NavMenu = ({ menu, onShiftTab }: NavMenuProps) => {
  const { t } = useTranslation('common');
  const router = useRouter();
  const isRestricted = useIsRestricted(false);
  const [, setModalOpen] = usePleaseLogIn();

  useEffect(() => {
    const navLinks = document.querySelectorAll('.nav-link');

    navLinks.forEach((link) => {
      const href = link.getAttribute('href');

      link.classList.remove('active');
      (link as HTMLElement).style.fontSize = '14px';
      link.removeAttribute('data-status');
      link.removeAttribute('aria-current');

      if (href === router.pathname) {
        link.classList.add('active');
      }
    });
  }, [router.pathname]);

  const [isHamburgerMenuExpanded, setHamburgerMenuExpanded] = useState(false);

  return (
    <nav css={navMenuStyles}>
      {menu !== 'patients' && (
        <div className="desktop">
          {menuList[menu]?.map((item, index) => {
            if (item.restricted && isRestricted) {
              return (
                <UnstyledButton
                  key={`${item.link}-${item.testid}`}
                  className="nav-link"
                  onClick={() => setModalOpen(true)}
                  data-testid={item.testid}
                >
                  {item.name}
                </UnstyledButton>
              );
            }

            return (
              <Link
                key={`${item.link}-${item.testid}`}
                href={item.link}
                className="nav-link"
                data-testid={item.testid}
                onKeyDown={(e) => {
                  if (index === 0 && e.key === 'Tab' && e.shiftKey) onShiftTab();
                }}
              >
                {item.name}
              </Link>
            );
          })}
        </div>
      )}

      {menu !== 'patients' && (
        <div className="mobile">
          <Dropdown
            aria-label={t('Navigation menu')}
            menu={{ items: mobileNavMenuDropdownMenu(menu, isRestricted, setModalOpen) }}
            arrow
            trigger={['click']}
            placement="top"
            onOpenChange={setHamburgerMenuExpanded}
          >
            <UnstyledButton
              aria-expanded={isHamburgerMenuExpanded}
              className="hamburger"
              aria-label={t('hamburger')}
              onClick={(e) => e.preventDefault()}
              data-testid="hamburger-menu"
            >
              <MenuOutlined />
            </UnstyledButton>
          </Dropdown>
        </div>
      )}
    </nav>
  );
};

interface UserMenuProps {
  pageType: PageType;
  isNavigationPanelVisible?: boolean;
  userLogout: () => Promise<void>;
  forceShowLogo?: boolean;
  testId?: string;
}

const StyledLogoImage = styled('img')`
  height: 25px;

  @media (min-width: 320px) {
    height: 35px;
  }

  @media (min-width: 375px) {
    height: 55px;
  }
`;

const MobilePatientHeader = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    padding: 1em;
    background: var(--white);
    color: var(--black);
  }
`;

const MobileContactLink = styled.a`
  color: var(--productPrimary);
  background-color: var(--white);
  border: 1px solid var(--productGray);
  font-size: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  flex-shrink: 0;
`;

const MobileLogoContainer = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
`;

const MobileLogoLink = styled(Link)`
  color: var(--black);
  font-weight: 500;
  font-size: 12px;
  text-align: center;

  &:hover {
    opacity: 1 !important;
    text-decoration: none !important;
  }
`;

const MobileServiceName = styled.span`
  font-weight: bold;
  @media (max-width: 576px) {
    font-size: 10px;
  }
`;

const MobileUserMenuContainer = styled.div`
  display: flex;
  align-items: center;
`;

export const UserMenu = ({
  pageType,
  isNavigationPanelVisible,
  userLogout,
  forceShowLogo = false,
  testId = 'full-user-menu'
}: UserMenuProps) => {
  const { t } = useTranslation('common');
  const isRestricted = useIsRestricted(false);

  const [isUserMenuExpanded, setUserMenuExpanded] = useState(false);
  const [isModalOpen, setModalOpen] = useState(false);

  const { user } = useAuth();
  const avatar = useAvatar(user?.user_guid || '');

  if (pageType === 'login' || forceShowLogo) {
    return (
      <div className="logo" data-testid="only-show-logo">
        <StyledLogoImage height={55} src={NEW_HBS_LOGO} alt={t('HBS UK logo')} />
      </div>
    );
  }

  if (isNavigationPanelVisible === false) {
    return null;
  }

  const userMenuDropdownMenu = (): MenuProps['items'] => {
    const menuItems: MenuProps['items'] = [];

    if (!isRestricted) {
      menuItems.push({
        key: 1,
        label: (
          <Link href={pageType === 'patients' ? '/patients/profile' : '/clinician/profile'}>{t('My profile')}</Link>
        ),
        icon: <UserOutlined />
      });
    }

    if (pageType === 'patients' && !isRestricted) {
      menuItems.push({
        key: 3,
        label: (
          <a rel="noreferrer" target="_blank" href="/patients/legal/?all_policies=0">
            {t('Legal')}
          </a>
        ),
        icon: <FileTextOutlined />
      });
    }

    menuItems.push({
      key: 4,
      label: (
        <a // NOSONAR
          data-testid="logout-button"
        >
          {t('Logout')}
        </a>
      ),
      icon: <LogoutOutlined />,
      onClick: () => {
        if (sessionStorage.getItem('inCall')) {
          setModalOpen(true);
        } else {
          userLogout().then();
          if (pageType === 'patients') resetDocmanGPRejectedModal();
        }
      }
    });

    return menuItems;
  };

  return (
    <>
      <Dropdown
        menu={{ items: userMenuDropdownMenu() }}
        arrow
        placement="bottomRight"
        trigger={['click']}
        onOpenChange={setUserMenuExpanded}
      >
        <UnstyledButton
          aria-expanded={isUserMenuExpanded}
          aria-label={t('User menu')}
          data-testid={testId}
          className="profile-button"
          onClick={(e) => e.preventDefault()}
          css={css`
            color: var(--black);
            display: flex;
            align-items: center;
            gap: 0.5em;
            white-space: nowrap;
          `}
        >
          <Avatar src={avatar} size="small" />
          <span
            className="name"
            css={css`
              font-size: 14px;

              @media (max-width: 1024px) {
                display: none;
              }
            `}
          >
            {user?.first_name && user?.last_name ? `${user.first_name} ${user.last_name}` : t('Guest Patient')}
            {sessionStorage?.getItem('mocked') === 'true' ? t(' (Mocked)') : ''}
          </span>
          <DownOutlined style={{ width: '10px', height: '10px' }} />
        </UnstyledButton>
      </Dropdown>

      <ConfirmationModal
        title="Logout"
        open={isModalOpen}
        okText="Logout"
        closable={false}
        onOk={() => {
          setModalOpen(false);
          userLogout().then();
        }}
        onCancel={() => setModalOpen(false)}
      >
        <p>{t('Logging out will end the call')}</p>
      </ConfirmationModal>
    </>
  );
};

interface HeadersProps {
  className?: string;
  pageType: PageType;
  pageTitle?: string;
  forceShowLogo?: boolean;
}

const Headers = ({ className = '', pageType, pageTitle = '', forceShowLogo = false }: HeadersProps) => {
  const { t, ready } = useTranslation('common');
  const dispatch = useDispatch();
  const { user } = useAuth();
  const router = useRouter();
  const service_name = user?.service_name || '';
  const queryClient = useQueryClient();
  const { theme, isHBSTheme } = useTheme();
  const [signInModalOpen, setSignInModalOpen] = usePleaseLogInAndRedirect();
  const [showSkipToContent, setShowSkipToContent] = useState(false);
  const [showAccessibilityOptions, setShowAccessibilityOptions] = useState(false);
  const isExtremeZoom = useExtremeZoom();
  const isStandalone = useIsStandalone();
  const { policiesAccepted } = usePolicies();
  const isClinicianSubdomain = useIsClinicianSubdomain();

  // Rebrand CHECK: Determine if the clinician layout needs to morph into the patient layout structure
  const isRebrand =
    pageType === 'patients' ||
    (pageType === 'clinician' && (theme?.id === 'axa' || theme?.id === 'hbs') && user?.user_type_code === 'CLINICIAN');

  useEffect(() => {
    if (showSkipToContent) {
      document.getElementById('skip-navigation-link')?.focus();
    }
  }, [showSkipToContent]);
  useEffect(() => {
    if (showAccessibilityOptions) {
      document.getElementById('accessibility-options-link')?.focus();
    }
  }, [showAccessibilityOptions]);

  const userLogout = async () => {
    Lobby.clearAllLobbies();
    clearPreviouslyPromisedRooms().then();
    dispatch(requestSuccess(actionTypes.AUTH_SIGNED_OUT));
    const res = await dispatch(actions.userLogout(queryClient));
    if (res.type === actionTypes.AUTH_LOGOUT_SUCCESS) {
      sessionStorage.removeItem('tour-attributes-set');
      await router.push('/login');
    }
  };

  const { TEST_VIDEO_CALL_BUTTON, CONTACT_US_URL } = useRuntimeConfig();
  const testCallButtonEnabled = TEST_VIDEO_CALL_BUTTON && pageType === 'patients';

  return (
    <>
      <Head key={Number(ready)}>
        <title>
          {pageTitle
            ? `${t('AppTitleShort')} - ${pageTitle}`
            : `${t('AppTitleShort')} - ${pageType[0].toUpperCase() + pageType.substring(1)}`}
        </title>
        <meta
          property="og:title"
          content={
            pageTitle
              ? `${t('AppTitleShort')} - ${pageTitle}`
              : `${t('AppTitleShort')} - ${pageType[0].toUpperCase() + pageType.substring(1)}`
          }
          key="title"
        />
      </Head>

      <header
        className={className}
        css={
          isRebrand
            ? css`
                width: 100%;
                flex-wrap: wrap;
                flex-shrink: 0;

                position: sticky;
                top: 0;
                z-index: 100;

                @media (max-height: 450px) {
                  position: static;
                }
              `
            : ''
        }
      >
        {pageType === 'patients' && (
          <MobilePatientHeader>
            <MobileContactLink
              href={CONTACT_US_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={t('Contact Us')}
              data-testid="mobile-contact-us-link"
            >
              <PhoneOutlined />
            </MobileContactLink>

            <MobileLogoContainer>
              <MobileLogoLink href="/patients" aria-label={t('Dashboard')}>
                <img height={service_name ? 35 : 55} src={theme.logo} alt={theme?.altText ?? t('logo')} />
                <br />
                {!isClinicianSubdomain && service_name && (
                  <MobileServiceName dangerouslySetInnerHTML={{ __html: service_name }} />
                )}
              </MobileLogoLink>
            </MobileLogoContainer>

            <MobileUserMenuContainer>
              <UserMenu
                pageType={pageType}
                userLogout={userLogout}
                isNavigationPanelVisible={!!policiesAccepted}
                forceShowLogo={forceShowLogo}
                testId="mobile-patient-user-menu"
              />
            </MobileUserMenuContainer>
          </MobilePatientHeader>
        )}

        <div
          css={css`
            width: 100%;
            display: flex;
            justify-content: space-between;
            align-items: center;

            padding: 1em;
            background: var(--white);
            color: var(--black);

            ${
              pageType === 'patients'
                ? `
              @media (max-width: 768px) {
                display: none;
              }
            `
                : ''
            }
          `}
        >
          {pageType === 'login' ? (
            // Logo displayed on non-service pages such as /accessibility, patients/register - Should be AXA for HBS themes
            <div className="logo">
              <img
                height={service_name ? 35 : 55}
                src={isHBSTheme ? AXA_LOGO : theme.logo}
                alt={isHBSTheme ? t('AXA logo') : (theme?.altText ?? t('logo'))}
              />
            </div>
          ) : (
            <Link
              href={pageType === 'patients' ? '/patients' : '/clinician'}
              aria-label={t('Dashboard')}
              css={css`
                color: var(--black);
                font-weight: 500;
                font-size: 12px;
                width: 150px;
                white-space: pre;
              `}
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Tab' && !e.shiftKey && !showSkipToContent) {
                  e.preventDefault();
                  e.stopPropagation();

                  setShowSkipToContent(true);
                }
              }}
            >
              <img height={service_name ? 35 : 55} src={theme.logo} alt={theme?.altText ?? t('logo')} />
              <br />
              {!isClinicianSubdomain && service_name && (
                <span
                  css={css`
                    position: relative;
                    font-weight: bold;
                    @media (max-width: 576px) {
                      font-size: 10px;
                    }
                  `}
                  dangerouslySetInnerHTML={{ __html: service_name }}
                />
              )}
            </Link>
          )}
          <a
            css={css`
              position: absolute;
              left: 1rem;
              top: 80px;
              padding: 16px 8px;
              font-size: 16px;
              border-radius: 6px;
              border: 2px solid var(--productPrimaryDark);
              text-align: center;
              z-index: ${showSkipToContent ? 100 : -200};
              background-color: var(--white);
              opacity: ${showSkipToContent ? 1 : 0};
              pointer-events: none;
            `}
            id="skip-navigation-link"
            href="#page-content"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Tab' || e.key === 'Enter') {
                setShowSkipToContent(false);

                if (e.key === 'Tab' && !e.shiftKey) {
                  setShowAccessibilityOptions(true);
                }
              }
            }}
            onFocus={() => {
              setShowSkipToContent(true);
            }}
            onBlur={() => {
              setShowSkipToContent(false);
            }}
          >
            {t('Skip to Content')}
          </a>

          {pageType !== 'login' && pageType === 'clinician' && !isRebrand && (
            <NavMenu menu={pageType} onShiftTab={() => setShowAccessibilityOptions(true)} />
          )}

          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 10px;

              padding: 1em;
              background: var(--white);
              color: var(--black);
            `}
          >
            {isExtremeZoom &&
              isRebrand &&
              (pageType === 'patients' ? <NavigationHeader /> : <NavMenu menu={pageType} onShiftTab={() => {}} />)}

            {testCallButtonEnabled && <AutomatedClinicianLauncher />}

            <UserMenu
              pageType={pageType}
              userLogout={userLogout}
              isNavigationPanelVisible={!!policiesAccepted}
              forceShowLogo={forceShowLogo}
            />

            <PleaseLogIn
              open={!!signInModalOpen.pleaseLogIn}
              onCancel={() => {
                if (signInModalOpen.pleaseLogInRedirect) {
                  return router.push(signInModalOpen.pleaseLogInRedirect);
                }

                return setSignInModalOpen({ pleaseLogIn: false });
              }}
            />

            {isStandalone && (
              <Button
                onClick={() => window.location.reload()}
                icon={<ReloadOutlined />}
                shape="circle"
                type="primary"
                aria-label={t('Reload')}
                title="Reload"
                data-testid="refresh-window-button"
                css={css`
                  margin-left: 0.5em;
                `}
              />
            )}
          </div>
        </div>

        {isRebrand &&
          !isExtremeZoom &&
          (pageType === 'patients' ? (
            !!policiesAccepted && <NavigationHeader pageType="patients" />
          ) : (
            <NavigationHeader pageType="clinician" />
          ))}
      </header>
    </>
  );
};

export default Headers;
